import type { PenAnchorPoint } from '@/lib/elements/types/pen-anchor-point'
import { NODE_ACCENT_COLOR } from '../constants/node-chrome'
import type { PenHoverTarget } from '../types/node-edit-targets'

/** Ring colour over an anchor that a click would remove. */
const REMOVE_COLOR = '#DC2626'

interface PenHoverHighlightProps {
  penHover: PenHoverTarget
  points: PenAnchorPoint[]
  /** Shape origin the (shape-local) points are offset by. */
  ox: number
  oy: number
  /** Half an anchor marker's size (canvas units). */
  anchorHalf: number
  /** Base stroke width (canvas units). */
  sw: number
}

interface HoverRingProps {
  pt: PenAnchorPoint | undefined
  ox: number
  oy: number
  anchorHalf: number
  sw: number
  color: string
}

/** A ring around the hovered anchor (nothing if the index went stale). */
function HoverRing({ pt, ox, oy, anchorHalf, sw, color }: HoverRingProps) {
  if (!pt) return null
  const cx = pt.x + ox
  const cy = pt.y + oy
  const ringR = anchorHalf * 1.8

  return (
    <circle
      cx={cx}
      cy={cy}
      r={ringR}
      fill="none"
      stroke={color}
      strokeWidth={sw * 1.5}
      pointerEvents="none"
    />
  )
}

/** Pen-edit mode visual highlights: an accent ring on an endpoint (continue), a red ring on an
 *  anchor (remove), or a filled dot where a click would insert a node on a segment. */
export function PenHoverHighlight({
  penHover,
  points,
  ox,
  oy,
  anchorHalf,
  sw,
}: PenHoverHighlightProps) {
  return (
    <>
      {penHover?.kind === 'endpoint' && (
        <HoverRing
          pt={points[penHover.index]}
          ox={ox}
          oy={oy}
          anchorHalf={anchorHalf}
          sw={sw}
          color={NODE_ACCENT_COLOR}
        />
      )}

      {penHover?.kind === 'anchor' && (
        <HoverRing
          pt={points[penHover.index]}
          ox={ox}
          oy={oy}
          anchorHalf={anchorHalf}
          sw={sw}
          color={REMOVE_COLOR}
        />
      )}

      {penHover?.kind === 'segment' && (
        <circle
          cx={penHover.hitX}
          cy={penHover.hitY}
          r={anchorHalf}
          fill={NODE_ACCENT_COLOR}
          stroke="white"
          strokeWidth={sw}
          pointerEvents="none"
        />
      )}
    </>
  )
}
